import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ROUTES } from '../../common-files/enums/server-routes';

// Central front-desk (Check-in / Check-out Operator) API calls. These routes are
// CENTRAL-scoped, so the x-establishment-id header (sent by the interceptor) is
// ignored by the backend here.
@Injectable({ providedIn: 'root' })
export class FrontdeskService {
  constructor(private http: HttpClient) {}

  fetchPremises(): Observable<any> {
    return this.http.get(ROUTES.FD_PREMISES);
  }

  // today's check-in / check-out counts. premises is a premises id, or 'all'.
  getDashboardCounts(premises: number | 'all'): Observable<any> {
    return this.http.get(`${ROUTES.FD_DASHBOARD_COUNTS}?premises=${premises}`);
  }

  // desk lists — payload { premises, date, familyCode, bookingId }.
  fetchCheckinList(payload: any): Observable<any> {
    return this.http.post(ROUTES.FD_CHECKIN_LIST, payload);
  }

  fetchCheckoutList(payload: any): Observable<any> {
    return this.http.post(ROUTES.FD_CHECKOUT_LIST, payload);
  }

  // guests + provisioned rooms for the check-in dialog's room allotment step.
  getCheckinContext(bookingId: number): Observable<any> {
    return this.http.get(`${ROUTES.FD_BOOKING}/${bookingId}/checkin-context`);
  }

  // perform check-in / check-out (payloads mirror the backend validators).
  checkin(payload: any): Observable<any> {
    return this.http.post(ROUTES.FD_CHECKIN, payload);
  }

  checkout(payload: any): Observable<any> {
    return this.http.post(ROUTES.FD_CHECKOUT, payload);
  }

  // guest stay history bucketed by state, looked up by Aadhaar.
  getGuestHistory(aadhaar: string): Observable<any> {
    const params = new HttpParams().set('aadhaar', aadhaar);
    return this.http.get(ROUTES.FD_GUEST_HISTORY, { params });
  }

  // the operator's own check-in/out actions (optional date range).
  getMyActivity(fromDate?: string, toDate?: string): Observable<any> {
    let params = new HttpParams();
    if (fromDate) params = params.set('fromDate', fromDate);
    if (toDate) params = params.set('toDate', toDate);
    return this.http.get(ROUTES.FD_MY_ACTIVITY, { params });
  }

  // self profile + OTP-verified mobile change / email change.
  getProfile(): Observable<any> {
    return this.http.get(ROUTES.FD_PROFILE);
  }

  requestMobileOtp(mobileNumber: string): Observable<any> {
    return this.http.post(ROUTES.FD_PROFILE_MOBILE_OTP, { mobileNumber });
  }

  verifyMobileOtp(otpId: number, otp: string): Observable<any> {
    return this.http.post(ROUTES.FD_PROFILE_MOBILE_VERIFY, { otpId, otp });
  }

  updateEmail(email: string): Observable<any> {
    return this.http.post(ROUTES.FD_PROFILE_EMAIL, { email });
  }

  // booking search (F1) — locate a booking by id or Aadhaar (provisioning reads).
  searchBookingById(bookingId: number): Observable<any> {
    return this.http.get(`${ROUTES.PROV_REQUISITION}/${bookingId}`);
  }

  searchBookingByAadhaar(aadhaar: string): Observable<any> {
    const params = new HttpParams().set('aadhaar', aadhaar);
    return this.http.get(`${ROUTES.PROV_REQUISITION}/by-aadhaar`, { params });
  }

  // re-trigger the booking confirmation SMS (only for CONFIRMED bookings).
  resendConfirmationSms(bookingId: number): Observable<any> {
    return this.http.post(`${ROUTES.FD_BOOKING}/${bookingId}/resend-sms`, {});
  }
}
